import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Plus, Trash2, Users, UserPlus, Mail, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { educationAPI } from '../api/axiosClient';
import { Parent, ParentDto, ParentRelationType, GetStudentDetailsResponse } from '../api/types';
import toast from 'react-hot-toast';

interface ParentAssignmentFormProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  onSuccess: () => void;
}

const ParentAssignmentForm: React.FC<ParentAssignmentFormProps> = ({
  isOpen,
  onClose,
  studentId,
  studentName,
  onSuccess
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Parent[]>([]);
  const [currentParents, setCurrentParents] = useState<ParentDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingParent, setIsAddingParent] = useState<string | null>(null);
  const [isRemovingParent, setIsRemovingParent] = useState<string | null>(null);

  const loadCurrentParents = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await educationAPI.getStudentDetails(studentId);
      if (response.success && response.data) {
        const studentData: GetStudentDetailsResponse = response.data;
        setCurrentParents(studentData.parents || []);
      }
    } catch (error: any) {
      console.error('Error loading current parents:', error);
      toast.error('Mevcut veliler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, [studentId]);

  // Load current parents when modal opens
  useEffect(() => {
    if (isOpen && studentId) {
      loadCurrentParents();
    }
  }, [isOpen, studentId, loadCurrentParents]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const response = await educationAPI.listParents({
        Q: searchTerm,
        Page: 1,
        Size: 10,
        OrderBy: 0
      });

      if (response.success && response.data) {
        // Filter out parents who are already assigned to this student
        const currentParentIds = currentParents.map(p => p.parentId);
        const availableParents = response.data.items.filter(
          (parent: Parent) => !currentParentIds.includes(parent.id)
        );
        setSearchResults(availableParents);
      }
    } catch (error: any) {
      console.error('Error searching parents:', error);
      toast.error('Veli arama sırasında hata oluştu');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddParent = async (parent: Parent) => {
    try {
      setIsAddingParent(parent.id);
      
      // For now, default to Mother relation type
      // In a real implementation, you might want to show a modal to select relation type
      const relationType = ParentRelationType.Mother;
      
      await educationAPI.addParentToStudent(studentId, parent.id, relationType);
      
      toast.success('Veli başarıyla eklendi');
      
      // Refresh current parents and search results
      await loadCurrentParents();
      setSearchResults(prev => prev.filter(p => p.id !== parent.id));
      
      onSuccess();
    } catch (error: any) {
      console.error('Error adding parent:', error);
      toast.error('Veli eklenirken hata oluştu');
    } finally {
      setIsAddingParent(null);
    }
  };

  const handleRemoveParent = async (parentId: string) => {
    try {
      setIsRemovingParent(parentId);
      
      await educationAPI.removeParentFromStudent(studentId, parentId);
      
      toast.success('Veli başarıyla kaldırıldı');
      
      // Refresh current parents
      await loadCurrentParents();
      
      onSuccess();
    } catch (error: any) {
      console.error('Error removing parent:', error);
      toast.error('Veli kaldırılırken hata oluştu');
    } finally {
      setIsRemovingParent(null);
    }
  };

  const getRelationTypeName = (relationType: ParentRelationType): string => {
    switch (relationType) {
      case ParentRelationType.Mother:
        return 'Anne';
      case ParentRelationType.Father:
        return 'Baba';
      case ParentRelationType.Guardian:
        return 'Vasi';
      case ParentRelationType.Other:
        return 'Diğer';
      default:
        return 'Bilinmiyor';
    }
  };

  const getRelationTypeColor = (relationType: ParentRelationType): string => {
    switch (relationType) {
      case ParentRelationType.Mother:
        return 'bg-pink-100 text-pink-800';
      case ParentRelationType.Father:
        return 'bg-blue-100 text-blue-800';
      case ParentRelationType.Guardian:
        return 'bg-green-100 text-green-800';
      case ParentRelationType.Other:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-6 h-6 mr-2 text-[#9B51E0]" />
              {studentName} - Veli Yönetimi
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Veli Ara ve Ekle</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Search Input */}
                <div className="flex items-center space-x-4">
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Veli adı, soyadı veya e-posta ile ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B51E0] focus:border-transparent"
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={isSearching || !searchTerm.trim()}
                    className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                  >
                    {isSearching ? 'Aranıyor...' : 'Ara'}
                  </Button>
                </div>

                {/* Search Results */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <Search className="w-4 h-4 mr-2 text-blue-600" />
                    Arama Sonuçları ({searchResults.length})
                  </h3>
                  
                  {searchResults.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">
                        {searchTerm ? 'Arama kriterlerine uygun veli bulunamadı' : 'Veli aramak için yukarıdaki arama kutusunu kullanın'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {searchResults.map((parent) => (
                        <div
                          key={parent.id}
                          className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                <UserPlus className="w-4 h-4 text-gray-600" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900 text-sm mb-1">
                                  {parent.fullName}
                                </h4>
                                <div className="space-y-1">
                                  <div className="flex items-center space-x-2">
                                    <Mail className="w-3 h-3 text-gray-400" />
                                    <p className="text-xs text-gray-600">
                                      {parent.email}
                                    </p>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Phone className="w-3 h-3 text-gray-400" />
                                    <p className="text-xs text-gray-600">
                                      {parent.phoneCode} {parent.phoneNumber}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleAddParent(parent)}
                              disabled={isAddingParent === parent.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                            >
                              {isAddingParent === parent.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              ) : (
                                <>
                                  <Plus className="w-4 h-4 mr-1" />
                                  Ekle
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Parents Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Users className="w-5 h-5 mr-2 text-green-600" />
                Mevcut Veliler ({currentParents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0]"></div>
                </div>
              ) : currentParents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Henüz veli atanmamış</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {currentParents.map((parent) => (
                    <div
                      key={parent.parentId}
                      className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-[#9B51E0] rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-1">
                              {parent.fullName}
                            </h4>
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <Mail className="w-3 h-3 text-gray-400" />
                                <p className="text-sm text-gray-600">
                                  {parent.email}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Phone className="w-3 h-3 text-gray-400" />
                                <p className="text-sm text-gray-600">
                                  {parent.phoneCode} {parent.phoneNumber}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRelationTypeColor(
                              parent.relationType
                            )}`}
                          >
                            {getRelationTypeName(parent.relationType)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveParent(parent.parentId)}
                            disabled={isRemovingParent === parent.parentId}
                          >
                            {isRemovingParent === parent.parentId ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ParentAssignmentForm;
